import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button, Card, Col, Descriptions, Form, Input, Modal, Popconfirm,
  Row, Select, Space, Switch, Table, Tag, Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { PartyCompositeFormFields, PARTY_TYPE_LABELS, PARTY_TYPE_OPTIONS } from "./PartyCompositeFormFields";

type PartyRow = {
  id: number; name: string; code: string; type: string;
  gstNumber?: string; pan?: string; contactPerson?: string;
  email?: string; phone?: string; addressLine1?: string;
  city?: string; state?: string; isActive: boolean;
};

const PARTY_TYPES = PARTY_TYPE_OPTIONS;

const TYPE_COLORS: Record<string, string> = {
  Customer: "blue", Vendor: "purple", Transporter: "orange",
  Person: "green", Branch: "cyan", Other: "default",
};

export const PartiesPage = () => {
  const [rows, setRows] = useState<PartyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listParties({ page: 1, pageSize: 500, q: search.trim() || undefined });
      setRows(result.data as PartyRow[]);
      setPage(1);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load parties"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesType = !typeFilter || row.type === typeFilter;
      const matchesSearch = !term || [
        row.name,
        row.code,
        row.phone,
        row.city,
        row.state,
        row.email,
        row.contactPerson,
      ].some((value) => (value ?? "").toLowerCase().includes(term));
      return matchesType && matchesSearch;
    });
  }, [rows, search, typeFilter]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ type: "Customer", registrationType: "Unregistered", country: "India", openingBalanceType: "Dr", bankAccounts: [], isActive: true });
    setDrawerOpen(true);
  };

  const openEdit = async (row: PartyRow) => {
    setEditingId(row.id);
    setDrawerOpen(true);
    try {
      const detail = await transportApi.getPartyFull(row.id);
      form.setFieldsValue({ ...detail, bankAccounts: detail.bankAccounts ?? [] });
    } catch (error) {
      setDrawerOpen(false);
      message.error(getApiErrorMessage(error, "Unable to load party details"));
    }
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editingId) {
        await transportApi.updatePartyFull(editingId, { ...values, bankAccounts: (values.bankAccounts ?? []).filter((bank: any) => bank.bankName || bank.accountNumber) });
        message.success("Party updated");
      } else {
        await transportApi.createPartyFull({ ...values, bankAccounts: (values.bankAccounts ?? []).filter((bank: any) => bank.bankName || bank.accountNumber) });
        message.success("Party created");
      }
      setDrawerOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      const conflict = (e as any)?.response?.data;
      if (conflict?.code === "DUPLICATE_PARTY_NAME" && conflict.existingParty) {
        const party = conflict.existingParty;
        Modal.warning({
          title: "Party already exists",
          width: 560,
          content: (
            <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
              <Descriptions.Item label="Name">{party.name}</Descriptions.Item>
              <Descriptions.Item label="Code">{party.code || "-"}</Descriptions.Item>
              <Descriptions.Item label="Relationship">{PARTY_TYPE_LABELS[party.type] ?? party.type}</Descriptions.Item>
              <Descriptions.Item label="GSTIN">{party.gstNumber || "-"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{party.phone || "-"}</Descriptions.Item>
              <Descriptions.Item label="Location">{[party.city, party.state].filter(Boolean).join(", ") || "-"}</Descriptions.Item>
              <Descriptions.Item label="Status">{party.isActive ? "Active" : "Inactive"}</Descriptions.Item>
            </Descriptions>
          ),
          okText: "Review Existing Party"
        });
        return;
      }
      message.error(getApiErrorMessage(e, "Unable to save party"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await transportApi.deleteParty(id);
      message.success("Party deactivated");
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to deactivate party"));
    }
  };

  const columns: ColumnsType<PartyRow> = [
    { title: "#", width: 70, render: (_, __, index) => ((page - 1) * pageSize) + index + 1 },
    { title: "Code", dataIndex: "code", width: 100 },
    { title: "Name", dataIndex: "name" },
    {
      title: "Type", dataIndex: "type", width: 110,
      render: (v: string) => <Tag color={TYPE_COLORS[v] ?? "default"}>{PARTY_TYPE_LABELS[v] ?? v}</Tag>,
    },
    { title: "GST", dataIndex: "gstNumber", width: 150 },
    { title: "Phone", dataIndex: "phone", width: 130 },
    { title: "City", dataIndex: "city", width: 110 },
    {
      title: "Status", dataIndex: "isActive", width: 80,
      render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "", width: 100,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" title="Edit party" icon={<EditOutlined />} onClick={() => void openEdit(row)} />
          <Popconfirm title="Deactivate this party?" onConfirm={() => void onDelete(row.id)} okButtonProps={{ danger: true }} okText="Deactivate">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Typography.Title level={3} style={{ margin: 0 }}>Party Registry</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Party</Button>
      </div>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="320px">
          <Input prefix={<SearchOutlined />} placeholder="Search name, code, phone, city..." allowClear value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col flex="180px">
          <Select placeholder="Filter by type" allowClear style={{ width: "100%" }} options={PARTY_TYPES} value={typeFilter} onChange={setTypeFilter} />
        </Col>
        <Col flex="auto">
          <Typography.Text type="secondary" style={{ lineHeight: "32px" }}>
            Showing {filteredRows.length} of {rows.length} loaded parties
          </Typography.Text>
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filteredRows}
        columns={columns}
        pagination={{
          current: page,
          total: filteredRows.length,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} parties`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />

      <StaticDrawer
        title={editingId ? "Edit Party" : "Add Party"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={680}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating Party..." : "Saving Party..."} onClick={() => void onSave()} /></div>}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="party-composite-form" initialValues={{ type: "Customer", registrationType: "Unregistered", country: "India", openingBalanceType: "Dr", bankAccounts: [], isActive: true }}>
          <PartyCompositeFormFields form={form} editing={Boolean(editingId)} />
        </Form>
      </StaticDrawer>
    </Card>
  );
};
